import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import type { Currency } from '@/data/inventory/types'
import { setContacts } from '@/data/sales/contacts'
import type {
  Contact,
  Deal,
  InteractionLog,
  InteractionType,
  InterestArea,
  LeadSource,
  LeadStage,
  Pipeline,
  Quote,
  QuoteLineItem,
  Task,
  TaskPriority,
} from '@/data/sales/types'


const PIPELINE_FIELDS = gql`
  fragment PipelineFields on Pipeline {
    id
    name
    description
    isDefault
    isActive
    stages {
      id
      name
      displayOrder
      defaultProbability
      isClosedWon
      isClosedLost
    }
  }
`

const GET_DEFAULT_PIPELINE = gql`
  query SalDefaultPipeline {
    defaultPipeline {
      ...PipelineFields
    }
  }
  ${PIPELINE_FIELDS}
`

const CONTACT_CORE_FIELDS = gql`
  fragment ContactCoreFields on Contact {
    id
    name
    email
    phone
    company
    lifecycleStage
    source
    tags
    assignedTo
    score
    isActive
    createdAt
    prefix
    alternatePhone
    addressArea
    addressCity
    addressState
    addressPincode
    firstContactDate
    lastContactDate
    interests
  }
`

const GET_CONTACTS = gql`
  query SalContacts {
    contacts(page: 0, size: 200) {
      content {
        ...ContactCoreFields
      }
    }
  }
  ${CONTACT_CORE_FIELDS}
`

const GET_DEALS = gql`
  query SalDeals {
    deals(page: 0, size: 200) {
      content {
        id
        name
        contactId
        contact { name }
        pipelineId
        stageId
        stageName
        value
        currency
        probability
        expectedCloseDate
        status
        lostReason
        ownerId
        closedDate
        createdAt
      }
    }
    pipelineValue
  }
`

const GET_QUOTES = gql`
  query SalQuotes {
    quotes(page: 0, size: 200) {
      content {
        id
        quoteNumber
        contactId
        contactName
        dealId
        currency
        subtotal
        discountAmount
        taxAmount
        totalAmount
        status
        approvalStatus
        validUntil
        notes
        rejectionReason
        createdAt
        lineItems {
          id
          sku
          description
          quantity
          unitOfMeasure
          unitPrice
          discountPercent
          taxRate
          lineTotal
        }
      }
    }
  }
`

const GET_TASKS = gql`
  query SalTasks {
    myTasks(page: 0, size: 100) {
      content {
        id
        title
        description
        contactId
        dealId
        assignedTo
        dueDate
        priority
        status
        completedAt
        createdAt
      }
    }
    overdueTasks {
      id
      title
      description
      contactId
      dealId
      assignedTo
      dueDate
      priority
      status
      completedAt
      createdAt
    }
    upcomingTasks(daysAhead: 14) {
      id
      title
      description
      contactId
      dealId
      assignedTo
      dueDate
      priority
      status
      completedAt
      createdAt
    }
  }
`

export const GET_CONTACT_DETAIL = gql`
  query SalContactDetail($id: ID!) {
    contact(id: $id) {
      ...ContactCoreFields
    }
    interactionsByContact(contactId: $id) {
      id
      contactId
      type
      subject
      summary
      interactionDate
      durationMinutes
      outcome
      createdAt
    }
    dealsByContact(contactId: $id) {
      id
      name
      contactId
      stageName
      value
      currency
      probability
      expectedCloseDate
      status
      createdAt
    }
    tasksByContact(contactId: $id) {
      id
      title
      dueDate
      priority
      status
      createdAt
    }
  }
  ${CONTACT_CORE_FIELDS}
`

export const GET_QUOTE_DETAIL = gql`
  query SalQuoteDetail($id: ID!) {
    quote(id: $id) {
      id
      quoteNumber
      contactId
      contactName
      dealId
      currency
      subtotal
      discountAmount
      taxAmount
      totalAmount
      status
      approvalStatus
      validUntil
      notes
      rejectionReason
      createdAt
      lineItems {
        id
        sku
        description
        quantity
        unitOfMeasure
        unitPrice
        discountPercent
        taxRate
        lineTotal
      }
    }
  }
`

const CREATE_CONTACT = gql`
  mutation SalCreateContact($input: CreateContactInput!) {
    createContact(input: $input) { id name lifecycleStage createdAt }
  }
`

const UPDATE_CONTACT = gql`
  mutation SalUpdateContact($input: UpdateContactInput!) {
    updateContact(input: $input) { id name email phone company lifecycleStage score }
  }
`

const UPDATE_CONTACT_STAGE = gql`
  mutation SalUpdateContactStage($contactId: ID!, $stage: LeadStage!) {
    updateContactStage(contactId: $contactId, stage: $stage) { id lifecycleStage }
  }
`

const LOG_INTERACTION = gql`
  mutation SalLogInteraction($input: CreateInteractionLogInput!) {
    logInteraction(input: $input) { id contactId type subject interactionDate }
  }
`

const SEND_CONTACT_EMAIL = gql`
  mutation SalSendContactEmail($contactId: ID!, $subject: String!, $body: String!) {
    sendContactEmail(contactId: $contactId, subject: $subject, body: $body) {
      id contactId type subject interactionDate
    }
  }
`

const CREATE_DEAL = gql`
  mutation SalCreateDeal($input: CreateDealInput!) {
    createDeal(input: $input) { id name status stageId createdAt }
  }
`

const MOVE_DEAL = gql`
  mutation SalMoveDeal($input: MoveDealStageInput!) {
    moveDealToStage(input: $input) { id stageId stageName probability }
  }
`

const MARK_WON = gql`
  mutation SalMarkWon($dealId: ID!) {
    markDealAsWon(dealId: $dealId) { id status closedDate }
  }
`

const MARK_LOST = gql`
  mutation SalMarkLost($input: MarkDealLostInput!) {
    markDealAsLost(input: $input) { id status lostReason }
  }
`

const CREATE_QUOTE = gql`
  mutation SalCreateQuote($input: CreateQuoteInput!) {
    createQuote(input: $input) { id quoteNumber status totalAmount }
  }
`

const APPROVE_QUOTE = gql`
  mutation SalApproveQuote($quoteId: ID!) {
    approveQuote(quoteId: $quoteId) { id status approvalStatus }
  }
`

const REJECT_QUOTE = gql`
  mutation SalRejectQuote($quoteId: ID!, $reason: String!) {
    rejectQuote(quoteId: $quoteId, reason: $reason) { id status rejectionReason }
  }
`

const SEND_QUOTE = gql`
  mutation SalSendQuote($quoteId: ID!) {
    sendQuote(quoteId: $quoteId) { id status }
  }
`

const ACCEPT_QUOTE = gql`
  mutation SalAcceptQuote($quoteId: ID!) {
    acceptQuote(quoteId: $quoteId) { id status }
  }
`

const CONVERT_QUOTE = gql`
  mutation SalConvertQuote($quoteId: ID!) {
    convertQuoteToInvoice(quoteId: $quoteId) { id status }
  }
`

const CREATE_TASK = gql`
  mutation SalCreateTask($input: CreateTaskInput!) {
    createTask(input: $input) { id title status dueDate }
  }
`

const COMPLETE_TASK = gql`
  mutation SalCompleteTask($taskId: ID!) {
    completeTask(taskId: $taskId) { id status completedAt }
  }
`

/* ── GQL shapes ── */

interface GqlContact {
  id: string
  name: string
  email?: string | null
  phone?: string | null
  company?: string | null
  lifecycleStage: LeadStage
  source?: LeadSource | null
  tags?: string[] | null
  assignedTo?: string | null
  score: number
  isActive: boolean
  createdAt?: string | null
  prefix?: string | null
  alternatePhone?: string | null
  addressArea?: string | null
  addressCity?: string | null
  addressState?: string | null
  addressPincode?: string | null
  firstContactDate?: string | null
  lastContactDate?: string | null
  interests?: InterestArea[] | null
}

interface GqlDeal {
  id: string
  name: string
  contactId: string
  contact?: { name: string } | null
  pipelineId: string
  stageId: string
  stageName?: string | null
  value: number
  currency: string
  probability: number
  expectedCloseDate?: string | null
  status: Deal['status']
  lostReason?: string | null
  ownerId?: string | null
  closedDate?: string | null
  createdAt?: string | null
}

interface GqlQuote {
  id: string
  quoteNumber: string
  contactId: string
  contactName?: string | null
  dealId?: string | null
  currency: string
  subtotal: number
  discountAmount: number
  taxAmount: number
  totalAmount: number
  status: Quote['status']
  approvalStatus?: Quote['approvalStatus']
  validUntil?: string | null
  notes?: string | null
  rejectionReason?: string | null
  createdAt?: string | null
  lineItems?: GqlQuoteLine[]
}

interface GqlQuoteLine {
  id: string
  sku?: string | null
  description: string
  quantity: number
  unitOfMeasure?: string | null
  unitPrice: number
  discountPercent?: number | null
  taxRate?: number | null
  lineTotal?: number | null
}

interface GqlTask {
  id: string
  title: string
  description?: string | null
  contactId?: string | null
  dealId?: string | null
  assignedTo: string
  dueDate?: string | null
  priority: TaskPriority
  status: Task['status']
  completedAt?: string | null
  createdAt?: string | null
}

function mapContact(c: GqlContact): Contact {
  return {
    id: c.id,
    name: c.name,
    email: c.email,
    phone: c.phone,
    company: c.company,
    lifecycleStage: c.lifecycleStage,
    source: c.source,
    tags: c.tags ?? [],
    assignedTo: c.assignedTo,
    score: c.score ?? 0,
    isActive: c.isActive,
    createdAt: c.createdAt ?? new Date().toISOString(),
    prefix: c.prefix,
    alternatePhone: c.alternatePhone,
    addressArea: c.addressArea,
    addressCity: c.addressCity,
    addressState: c.addressState,
    addressPincode: c.addressPincode,
    firstContactDate: c.firstContactDate,
    lastContactDate: c.lastContactDate,
    interests: c.interests ?? [],
  }
}

function mapDeal(d: GqlDeal): Deal {
  return {
    id: d.id,
    name: d.name,
    contactId: d.contactId,
    contactName: d.contact?.name,
    pipelineId: d.pipelineId,
    stageId: d.stageId,
    stageName: d.stageName,
    value: Number(d.value) || 0,
    currency: d.currency as Currency,
    probability: Number(d.probability) || 0,
    expectedCloseDate: d.expectedCloseDate,
    status: d.status,
    lostReason: d.lostReason,
    ownerId: d.ownerId,
    closedDate: d.closedDate,
    createdAt: d.createdAt ?? new Date().toISOString(),
  }
}

function mapQuoteLine(l: GqlQuoteLine): QuoteLineItem {
  return {
    id: l.id,
    sku: l.sku,
    description: l.description,
    quantity: Number(l.quantity) || 0,
    unitOfMeasure: l.unitOfMeasure,
    unitPrice: Number(l.unitPrice) || 0,
    discountPercent: l.discountPercent,
    taxRate: l.taxRate,
    lineTotal: l.lineTotal != null ? Number(l.lineTotal) : undefined,
  }
}

function mapQuote(q: GqlQuote): Quote {
  return {
    id: q.id,
    quoteNumber: q.quoteNumber,
    contactId: q.contactId,
    contactName: q.contactName,
    dealId: q.dealId,
    currency: q.currency as Currency,
    lineItems: (q.lineItems ?? []).map(mapQuoteLine),
    subtotal: Number(q.subtotal) || 0,
    discountAmount: Number(q.discountAmount) || 0,
    taxAmount: Number(q.taxAmount) || 0,
    totalAmount: Number(q.totalAmount) || 0,
    status: q.status,
    approvalStatus: q.approvalStatus,
    validUntil: q.validUntil,
    notes: q.notes,
    rejectionReason: q.rejectionReason,
    createdAt: q.createdAt ?? new Date().toISOString(),
  }
}

function mapTask(t: GqlTask): Task {
  return {
    id: t.id,
    title: t.title,
    description: t.description,
    contactId: t.contactId,
    dealId: t.dealId,
    assignedTo: t.assignedTo,
    dueDate: t.dueDate,
    priority: t.priority,
    status: t.status,
    completedAt: t.completedAt,
    createdAt: t.createdAt ?? new Date().toISOString(),
  }
}

function mergeTasks(...lists: GqlTask[][]): Task[] {
  const seen = new Map<string, Task>()
  for (const list of lists) {
    for (const t of list) seen.set(t.id, mapTask(t))
  }
  return [...seen.values()]
}

/* ── Context ── */

export interface CreateContactInput {
  name: string
  email?: string
  phone?: string
  company?: string
  source?: LeadSource
  tags?: string[]
  prefix?: string
  alternatePhone?: string
  addressArea?: string
  addressCity?: string
  addressState?: string
  addressPincode?: string
  firstContactDate?: string
  interests?: InterestArea[]
}

export interface CreateDealInput {
  name: string
  contactId: string
  pipelineId?: string
  stageId?: string
  value?: number
  currency?: Currency
  expectedCloseDate?: string | null
}

export interface CreateQuoteInput {
  contactId: string
  dealId?: string | null
  currency: Currency
  lineItems: QuoteLineItem[]
  validUntil?: string | null
  notes?: string
}

export interface CreateTaskInput {
  title: string
  description?: string
  contactId?: string
  dealId?: string
  dueDate?: string
  priority?: TaskPriority
}

export interface LogInteractionInput {
  contactId: string
  type: InteractionType
  subject?: string
  summary?: string
  interactionDate: string
  durationMinutes?: number
  outcome?: string
}

interface SalesContextValue {
  pipeline: Pipeline | null
  contacts: Contact[]
  deals: Deal[]
  quotes: Quote[]
  tasks: Task[]
  pipelineValue: number
  loading: boolean
  createContact: (input: CreateContactInput) => Promise<Contact>
  updateContact: (id: string, patch: Partial<CreateContactInput>) => Promise<void>
  updateContactStage: (contactId: string, stage: LeadStage) => Promise<void>
  logInteraction: (input: LogInteractionInput) => Promise<void>
  sendContactEmail: (contactId: string, subject: string, body: string) => Promise<void>
  createDeal: (input: CreateDealInput) => Promise<Deal>
  moveDealToStage: (dealId: string, stageId: string) => Promise<void>
  markDealWon: (dealId: string) => Promise<void>
  markDealLost: (dealId: string, reason: string) => Promise<void>
  createQuote: (input: CreateQuoteInput) => Promise<Quote>
  approveQuote: (quoteId: string) => Promise<void>
  rejectQuote: (quoteId: string, reason: string) => Promise<void>
  sendQuote: (quoteId: string) => Promise<void>
  acceptQuote: (quoteId: string) => Promise<void>
  convertQuote: (quoteId: string) => Promise<void>
  createTask: (input: CreateTaskInput) => Promise<void>
  completeTask: (taskId: string) => Promise<void>
  refetchAll: () => Promise<void>
}

const SalesContext = createContext<SalesContextValue | null>(null)

export function SalesProvider({ children }: { children: ReactNode }) {
  const pipelineQ = useQuery(GET_DEFAULT_PIPELINE)
  const contactsQ = useQuery(GET_CONTACTS)
  const dealsQ = useQuery(GET_DEALS)
  const quotesQ = useQuery(GET_QUOTES)
  const tasksQ = useQuery(GET_TASKS)

  const [createContactMut] = useMutation(CREATE_CONTACT)
  const [updateContactMut] = useMutation(UPDATE_CONTACT)
  const [updateContactStageMut] = useMutation(UPDATE_CONTACT_STAGE)
  const [logInteractionMut] = useMutation(LOG_INTERACTION)
  const [sendContactEmailMut] = useMutation(SEND_CONTACT_EMAIL)
  const [createDealMut] = useMutation(CREATE_DEAL)
  const [moveDealMut] = useMutation(MOVE_DEAL)
  const [markWonMut] = useMutation(MARK_WON)
  const [markLostMut] = useMutation(MARK_LOST)
  const [createQuoteMut] = useMutation(CREATE_QUOTE)
  const [approveQuoteMut] = useMutation(APPROVE_QUOTE)
  const [rejectQuoteMut] = useMutation(REJECT_QUOTE)
  const [sendQuoteMut] = useMutation(SEND_QUOTE)
  const [acceptQuoteMut] = useMutation(ACCEPT_QUOTE)
  const [convertQuoteMut] = useMutation(CONVERT_QUOTE)
  const [createTaskMut] = useMutation(CREATE_TASK)
  const [completeTaskMut] = useMutation(COMPLETE_TASK)

  const pipeline: Pipeline | null = pipelineQ.data?.defaultPipeline ?? null
  const contacts = useMemo(() => {
    const raw: GqlContact[] = contactsQ.data?.contacts?.content ?? []
    const mapped = raw.map(mapContact)
    setContacts(mapped)
    return mapped
  }, [contactsQ.data])

  const deals = useMemo(() => {
    const raw: GqlDeal[] = dealsQ.data?.deals?.content ?? []
    return raw.map(mapDeal)
  }, [dealsQ.data])

  const quotes = useMemo(() => {
    const raw: GqlQuote[] = quotesQ.data?.quotes?.content ?? []
    return raw.map(mapQuote)
  }, [quotesQ.data])

  const tasks = useMemo(() => {
    const d = tasksQ.data
    return mergeTasks(
      d?.myTasks?.content ?? [],
      d?.overdueTasks ?? [],
      d?.upcomingTasks ?? [],
    )
  }, [tasksQ.data])

  const pipelineValue = Number(dealsQ.data?.pipelineValue) || 0

  const refetchAll = useCallback(async () => {
    await Promise.all([
      pipelineQ.refetch(),
      contactsQ.refetch(),
      dealsQ.refetch(),
      quotesQ.refetch(),
      tasksQ.refetch(),
    ])
  }, [pipelineQ, contactsQ, dealsQ, quotesQ, tasksQ])

  const createContact = useCallback<SalesContextValue['createContact']>(
    async (input) => {
      const { data } = await createContactMut({
        // assignedTo is deliberately omitted — ContactApplicationService assigns the signed-in user
        // when it is null. Sending a fixed id assigned every contact to a user that does not exist.
        variables: { input },
      })
      await refetchAll()
      const c = data?.createContact
      return mapContact({
        ...input,
        id: c?.id ?? '',
        lifecycleStage: c?.lifecycleStage ?? 'LEAD',
        score: 0,
        isActive: true,
        tags: input.tags ?? [],
        createdAt: c?.createdAt,
      } as GqlContact)
    },
    [createContactMut, refetchAll],
  )

  const updateContact = useCallback<SalesContextValue['updateContact']>(
    async (id, patch) => {
      await updateContactMut({ variables: { input: { id, ...patch } } })
      await refetchAll()
    },
    [updateContactMut, refetchAll],
  )

  const updateContactStage = useCallback<SalesContextValue['updateContactStage']>(
    async (contactId, stage) => {
      await updateContactStageMut({ variables: { contactId, stage } })
      await refetchAll()
    },
    [updateContactStageMut, refetchAll],
  )

  const logInteraction = useCallback<SalesContextValue['logInteraction']>(
    async (input) => {
      await logInteractionMut({ variables: { input } })
      await refetchAll()
    },
    [logInteractionMut, refetchAll],
  )

  const sendContactEmail = useCallback<SalesContextValue['sendContactEmail']>(
    async (contactId, subject, body) => {
      await sendContactEmailMut({ variables: { contactId, subject, body } })
      // The send is logged as an interaction server-side, so refresh to pick it up on the timeline.
      await refetchAll()
    },
    [sendContactEmailMut, refetchAll],
  )

  const createDeal = useCallback<SalesContextValue['createDeal']>(
    async (input) => {
      const defaultStage = pipeline?.stages.find((s) => !s.isClosedWon && !s.isClosedLost)
      const { data } = await createDealMut({
        variables: {
          input: {
            name: input.name,
            contactId: input.contactId,
            pipelineId: input.pipelineId ?? pipeline?.id,
            stageId: input.stageId ?? defaultStage?.id,
            value: input.value,
            currency: input.currency ?? 'USD',
            expectedCloseDate: input.expectedCloseDate,
          },
        },
      })
      await refetchAll()
      const d = data?.createDeal
      return mapDeal({
        id: d?.id ?? '',
        name: input.name,
        contactId: input.contactId,
        pipelineId: input.pipelineId ?? pipeline?.id ?? '',
        stageId: d?.stageId ?? input.stageId ?? '',
        value: input.value ?? 0,
        currency: input.currency ?? 'USD',
        probability: 10,
        status: d?.status ?? 'OPEN',
        createdAt: d?.createdAt,
      } as GqlDeal)
    },
    [createDealMut, pipeline, refetchAll],
  )

  const moveDealToStage = useCallback<SalesContextValue['moveDealToStage']>(
    async (dealId, stageId) => {
      await moveDealMut({ variables: { input: { dealId, stageId } } })
      await refetchAll()
    },
    [moveDealMut, refetchAll],
  )

  const markDealWon = useCallback<SalesContextValue['markDealWon']>(
    async (dealId) => {
      await markWonMut({ variables: { dealId } })
      await refetchAll()
    },
    [markWonMut, refetchAll],
  )

  const markDealLost = useCallback<SalesContextValue['markDealLost']>(
    async (dealId, reason) => {
      await markLostMut({ variables: { input: { dealId, lostReason: reason } } })
      await refetchAll()
    },
    [markLostMut, refetchAll],
  )

  const createQuote = useCallback<SalesContextValue['createQuote']>(
    async (input) => {
      const { data } = await createQuoteMut({
        variables: {
          input: {
            contactId: input.contactId,
            dealId: input.dealId,
            currency: input.currency,
            validUntil: input.validUntil,
            notes: input.notes,
            lineItems: input.lineItems.map((l) => ({
              sku: l.sku,
              description: l.description,
              quantity: l.quantity,
              unitOfMeasure: l.unitOfMeasure ?? 'EA',
              unitPrice: l.unitPrice,
              discountPercent: l.discountPercent,
              taxRate: l.taxRate,
            })),
          },
        },
      })
      await refetchAll()
      const q = data?.createQuote
      return mapQuote({
        id: q?.id ?? '',
        quoteNumber: q?.quoteNumber ?? '',
        contactId: input.contactId,
        currency: input.currency,
        lineItems: input.lineItems,
        subtotal: 0,
        discountAmount: 0,
        taxAmount: 0,
        totalAmount: Number(q?.totalAmount) || 0,
        status: q?.status ?? 'DRAFT',
        createdAt: new Date().toISOString(),
      } as GqlQuote)
    },
    [createQuoteMut, refetchAll],
  )

  const approveQuote = useCallback(async (quoteId: string) => {
    await approveQuoteMut({ variables: { quoteId } })
    await refetchAll()
  }, [approveQuoteMut, refetchAll])

  const rejectQuote = useCallback(async (quoteId: string, reason: string) => {
    await rejectQuoteMut({ variables: { quoteId, reason } })
    await refetchAll()
  }, [rejectQuoteMut, refetchAll])

  const sendQuote = useCallback(async (quoteId: string) => {
    await sendQuoteMut({ variables: { quoteId } })
    await refetchAll()
  }, [sendQuoteMut, refetchAll])

  const acceptQuote = useCallback(async (quoteId: string) => {
    await acceptQuoteMut({ variables: { quoteId } })
    await refetchAll()
  }, [acceptQuoteMut, refetchAll])

  const convertQuote = useCallback(async (quoteId: string) => {
    await convertQuoteMut({ variables: { quoteId } })
    await refetchAll()
  }, [convertQuoteMut, refetchAll])

  const createTask = useCallback<SalesContextValue['createTask']>(
    async (input) => {
      await createTaskMut({
        variables: {
          input: {
            ...input,
            // assignedTo omitted on purpose: TaskApplicationService assigns the signed-in user when
            // it is null. A hardcoded id assigned every task to a user nobody signs in as, so the
            // Tasks page (which reads `myTasks`, filtered by the current user) never showed them —
            // while the mutation succeeded and the UI reported success.
            priority: input.priority ?? 'MEDIUM',
          },
        },
      })
      await refetchAll()
    },
    [createTaskMut, refetchAll],
  )

  const completeTask = useCallback(async (taskId: string) => {
    await completeTaskMut({ variables: { taskId } })
    await refetchAll()
  }, [completeTaskMut, refetchAll])

  const value = useMemo<SalesContextValue>(
    () => ({
      pipeline,
      contacts,
      deals,
      quotes,
      tasks,
      pipelineValue,
      loading: pipelineQ.loading || contactsQ.loading || dealsQ.loading || quotesQ.loading || tasksQ.loading,
      createContact,
      updateContact,
      updateContactStage,
      logInteraction,
      sendContactEmail,
      createDeal,
      moveDealToStage,
      markDealWon,
      markDealLost,
      createQuote,
      approveQuote,
      rejectQuote,
      sendQuote,
      acceptQuote,
      convertQuote,
      createTask,
      completeTask,
      refetchAll,
    }),
    [
      pipeline, contacts, deals, quotes, tasks, pipelineValue,
      pipelineQ.loading, contactsQ.loading, dealsQ.loading, quotesQ.loading, tasksQ.loading,
      createContact, updateContact, updateContactStage, logInteraction, sendContactEmail,
      createDeal, moveDealToStage, markDealWon, markDealLost,
      createQuote, approveQuote, rejectQuote, sendQuote, acceptQuote, convertQuote,
      createTask, completeTask, refetchAll,
    ],
  )

  return <SalesContext.Provider value={value}>{children}</SalesContext.Provider>
}

export function useSales(): SalesContextValue {
  const ctx = useContext(SalesContext)
  if (!ctx) throw new Error('useSales must be used within SalesProvider')
  return ctx
}

export function mapInteractionLog(i: {
  id: string
  contactId: string
  type: InteractionType
  subject?: string | null
  summary?: string | null
  interactionDate: string
  durationMinutes?: number | null
  outcome?: string | null
  createdAt?: string | null
}): InteractionLog {
  return {
    id: i.id,
    contactId: i.contactId,
    type: i.type,
    subject: i.subject,
    summary: i.summary,
    interactionDate: i.interactionDate,
    durationMinutes: i.durationMinutes,
    outcome: i.outcome,
    createdAt: i.createdAt ?? i.interactionDate,
  }
}
